// @ts-nocheck
// Ported byte-identically from v1 views/dashboard/ctiHelpers.js.
// Pure UI helpers used by NavBar + the CTI screen tree — formatting,
// button-class chooser, control icons.
import React from 'react';
import { CALL_STATUS_LABELS } from '@/utilities/callStatus';
import { FiMic, FiPlay, FiVolume2, FiX } from 'react-icons/fi';
import { HiOutlinePause } from 'react-icons/hi2';
import { LuPanelRightOpen } from 'react-icons/lu';
import { maskNumber } from '@/utilities/cliDisplay';

export function ContactNumberInput({ value, originalValue, maskNumbers, onChange, ...rest }) {
  const original = originalValue == null ? '' : String(originalValue);
  const current = value == null ? '' : String(value);
  const hidden = Boolean(maskNumbers && original && current === original);
  return (
    <input
      {...rest}
      type="text"
      inputMode="tel"
      autoComplete="off"
      value={hidden ? '' : current}
      placeholder={hidden ? `${maskNumber(original)} (type to change)` : rest.placeholder}
      onChange={(e) => {
        const next = e.target.value.replace(/[^\d+]/g, '').slice(0, 15);
        onChange(maskNumbers && !next && original ? original : next);
      }}
    />
  );
}

export function formatSeconds(seconds) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

// Call duration = the leg's stored total_duration; rows stored before it existed fall back to billing.
export const callDurationSeconds = (call) =>
  call?.total_duration != null && call.total_duration !== ''
    ? Number(call.total_duration) || 0
    : Number(call?.billing_duration) || 0;

export { CALL_STATUS_LABELS };

// Stored CDR call status for the agent view: incoming = the agent leg's own call_status,
// outgoing = the customer leg's (other_party_call_status). null when not stored yet (old rows).
export const isIncomingDirection = (call) => {
  const dir = String(call?.direction || '').toLowerCase();
  return dir === 'incoming' || dir === 'inbound' || dir === 'in';
};

export const callRowCustomerNumber = (call) => {
  const dir = String(call?.direction || '').toLowerCase();
  const outgoing = dir === 'outgoing' || dir === 'outbound' || dir === 'out';
  return call?.number || (outgoing ? call?.callee_number : call?.caller_number) || '';
};

export const agentCallStatus = (call) => {
  const incoming = isIncomingDirection(call);
  const status = String((incoming ? call?.call_status : call?.other_party_call_status) || '').toLowerCase();
  return CALL_STATUS_LABELS[status] ? status : null;
};

export const callOutcome = (call) => {
  if (call?.call_outcome) return call.call_outcome;
  const status = agentCallStatus(call);
  if (status) return status === 'connected' ? 'answered' : status === 'abandoned' ? 'abandoned' : 'failed';
  const answered = String(call?.answer_status || '').toLowerCase() === 'answered' || Number(call?.billing_duration) > 0;
  return answered ? 'answered' : 'failed';
};

// Was border-black/[0.07] dark:border-white/[0.09] — technically visible
// but far weaker than the slate-blue-gray already proven to show up
// clearly on every other card in the CTI (cti.css/cti2.css use hardcoded
// rgba(100,116,139,*) / rgba(148,163,184,*) for exactly this reason).
// Matched to the same color here instead of continuing to tune a plain
// black/white opacity that kept reading as borderless.
export const HAIRLINE = 'border-[rgba(100,116,139,0.45)] dark:border-[rgba(148,163,184,0.35)]';
export const HAIRLINE_SOFT = 'border-[rgba(100,116,139,0.28)] dark:border-[rgba(148,163,184,0.22)]';

export const getIdleSeconds = (idleFrom) => {
  if (!idleFrom) return 0;

  const start = new Date(idleFrom).getTime();
  const now = Date.now();

  return Math.floor((now - start) / 1000);
};

export function toneStyle(tone) {
  if (tone === 'blue') return { background: 'var(--blue-lt)', color: 'var(--blue)' };
  if (tone === 'amber') return { background: 'var(--amber-lt)', color: 'var(--amber)' };
  if (tone === 'green') return { background: 'var(--green-lt)', color: 'var(--green)' };
  return { background: '#f3e8ff', color: '#7c3aed' };
}

export function controlIcon(key) {
  if (key === 'pause') return <HiOutlinePause />;
  if (key === 'play') return <FiPlay />;
  if (key === 'volume') return <FiVolume2 />;
  if (key === 'mic') return <FiMic />;
  if (key === 'leave') return <LuPanelRightOpen />;
  return <FiX />;
}

export function resolveCustomerName(callerContact, activeCallMeta) {
  if (callerContact) {
    const first = callerContact.first_name || '';
    const last = callerContact.last_name || '';
    const joined = `${first} ${last}`.trim();
    if (joined) return joined;
    if (callerContact.name) return callerContact.name;
  }
  return activeCallMeta?.caller_profile || null;
}

export function resolveCustomerNumber(callerContact, activeCallMeta) {
  return (
    callerContact?.primary_number ||
    activeCallMeta?.customer_number ||
    activeCallMeta?.caller_number ||
    null
  );
}

function outgoingLegPhase(outgoingCallStatus) {
  const raw = String(outgoingCallStatus || '').toLowerCase();
  if (!raw) return { text: 'Dialing…', tone: 'mute' };
  if (raw.includes('customer')) {
    if (raw.includes('connect')) return { text: 'Customer connected', tone: 'good' };
    if (raw.includes('ring')) return { text: 'Customer ringing', tone: 'warn' };
    return { text: 'Calling customer', tone: 'info' };
  }
  if (raw.includes('agent')) {
    if (raw.includes('ring')) return { text: 'Agent ringing', tone: 'mute' };
    if (raw.includes('connect')) return { text: 'Connecting…', tone: 'info' };
    return { text: 'Dialing…', tone: 'mute' };
  }
  if (raw.includes('accepted') || raw.includes('established')) return { text: 'Customer connected', tone: 'good' };
  // Pre-ring setup (WebRTC 'connecting' → 'calling' → SIP 100 'trying',
  // mobile 'dialing') — the far end isn't ringing yet, so match the Call
  // button's "Connecting…" until a real 180/183 arrives.
  if (['connecting', 'calling', 'trying', 'dialing'].includes(raw)) return { text: 'Connecting…', tone: 'info' };
  if (raw.includes('progress') || raw.includes('ring')) return { text: 'Customer ringing', tone: 'warn' };
  if (raw.includes('fail')) return { text: 'Call failed', tone: 'bad' };
  return { text: raw, tone: 'mute' };
}

export function resolveCallPhase({
  direction,
  callState,
  outgoingCallStatus,
  customerRingingActive,
  hasBridge,
  agentFirst = false,
  customerConnected = false,
}) {
  if (!callState || callState === 'idle') return { text: 'Ended', tone: 'mute' };
  if (callState === 'hold') return { text: 'On hold', tone: 'mute' };
  if (callState === 'conference') return { text: 'Conference', tone: 'info' };
  // The one case the customer's own line rings mid-call, in either
  // direction (blind transfer / consult target).
  if (customerRingingActive) return { text: 'Customer ringing', tone: 'warn' };
  if (callState === 'incoming') return { text: 'Agent ringing', tone: 'warn' };
  if (hasBridge) {
    if (agentFirst) {
      return customerConnected
        ? { text: 'Customer connected', tone: 'good' }
        : { text: 'Agent connected · calling customer', tone: 'info' };
    }
    return { text: direction === 'outgoing' ? 'Customer connected' : 'Connected', tone: 'good' };
  }
  if (direction === 'outgoing') return outgoingLegPhase(outgoingCallStatus);
  return { text: 'Ringing', tone: 'warn' };
}

export function callPhaseEvt({ text, tone }) {
  if (/ring/i.test(text)) return 'ring';
  if (text === 'On hold') return 'hold';
  if (text === 'Conference') return 'conf';
  if (text === 'Ended') return 'ended';
  return { good: 'live', bad: 'fail' }[tone] || 'dial';
}

export const callStateEvt = (callState) => ({
  incoming: 'ring', ringing: 'ring', oncall: 'live', connected: 'live',
  conference: 'conf', hold: 'hold', dialing: 'dial',
})[String(callState || '').toLowerCase()] || 'ended';

export const callOutcomeEvt = (label) => ({
  Answered: 'live', Connected: 'live', Missed: 'ring', Declined: 'fail', Failed: 'fail', Abandoned: 'hold',
})[label] || 'ended';

export function getScreenButtonClass(screenKey, activeScreen, parsedStatus) {
  if (parsedStatus.success === false) return 'disconnected-active';
  if (screenKey !== activeScreen) return '';
  if (screenKey === 'ringing') return 'ringing-active';
  if (screenKey === 'oncall') return 'oncall-active';
  if (screenKey === 'conference') return 'conf-active';
  return 'active';
}
